// Compares two logical models. Entities and properties are paired by name, then by declared
// renames, then by EF's own rename operations, then by similarity (marked as guesses).
// Database-specific `physical` details are ignored unless asked for.
import type { RenameHint } from '../ef/operations';
import { canonicalJson } from '../model/canonical';
import { isPureJoin, rootOfPath } from '../model/logical';
import type {
  ComplexSpec,
  EntitySpec,
  IndexSpec,
  MappingSpec,
  ModelSpec,
  OwnedSpec,
  PropertySpec,
  RelationshipSpec,
  RenameSpec,
  SequenceSpec,
} from '../model/schema';

export type MatchHow = 'name' | 'declared' | 'migration' | 'guess';

export type ModelChange =
  | { kind: 'entity-added'; entity: string }
  | { kind: 'entity-removed'; entity: string }
  | { kind: 'entity-renamed'; from: string; to: string; how: MatchHow; confidence: number }
  | { kind: 'entity-changed'; entity: string; facets: string[]; before: Partial<EntityFacets>; after: Partial<EntityFacets> }
  | { kind: 'inheritance-changed'; entity: string; facets: string[]; before: Partial<EntityFacets>; after: Partial<EntityFacets> }
  | { kind: 'property-added'; entity: string; property: string; spec: PropertySpec }
  | { kind: 'property-removed'; entity: string; property: string; spec: PropertySpec }
  | { kind: 'property-renamed'; entity: string; from: string; to: string; how: MatchHow; confidence: number }
  | { kind: 'property-changed'; entity: string; property: string; facets: string[]; before: PropertySpec; after: PropertySpec }
  | { kind: 'key-changed'; entity: string; before: string[]; after: string[] }
  | { kind: 'key-added' | 'key-removed'; entity: string; properties: string[] }
  | { kind: 'index-added' | 'index-removed'; entity: string; properties: string[]; spec: IndexSpec }
  | { kind: 'index-changed'; entity: string; properties: string[]; facets: string[]; before: IndexSpec; after: IndexSpec }
  | { kind: 'check-added' | 'check-removed'; entity: string; name: string; sql: string }
  | { kind: 'check-changed'; entity: string; name: string; before: string; after: string }
  | { kind: 'seed-changed'; entity: string; before: number; after: number }
  | { kind: 'owned-added' | 'owned-removed'; entity: string; navigation: string; type: string }
  | { kind: 'owned-changed'; entity: string; navigation: string; facets: string[] }
  | { kind: 'complex-added' | 'complex-removed'; entity: string; property: string; type: string }
  | { kind: 'complex-changed'; entity: string; property: string; facets: string[] }
  | { kind: 'relationship-added' | 'relationship-removed'; dependent: string; principal: string; foreignKey: string[]; relationship: RelationshipSpec }
  | { kind: 'relationship-changed'; dependent: string; principal: string; foreignKey: string[]; facets: string[]; before: RelationshipSpec; after: RelationshipSpec }
  | { kind: 'many-to-many-added' | 'many-to-many-removed'; between: [string, string]; join: string }
  | { kind: 'sequence-added' | 'sequence-removed'; name: string }
  | { kind: 'sequence-changed'; name: string; facets: string[] };

export type ChangeKind = ModelChange['kind'];

/** Entity-level facets that can change. */
export interface EntityFacets {
  base: string | null;
  strategy: string | null;
  discriminator: string | null;
  discriminatorValue: unknown;
  keyless: boolean;
  table: string | null;
  schema: string | null;
  view: string | null;
  json: string | null;
  comment: string | null;
}

export interface DiffOptions {
  /** Renames declared by the newer document (`renames` in efmodel). */
  renames?: RenameSpec[];
  /** Rename operations from the migration that produced the newer model. */
  hints?: RenameHint[];
  /** Also compare database-specific details. */
  includePhysical?: boolean;
  /** Report pure join tables as many-to-many links (default true). */
  collapseJoins?: boolean;
}

export interface EntityMatch {
  from: string;
  to: string;
  how: MatchHow;
  confidence: number;
}

export interface ModelDiff {
  changes: ModelChange[];
  /** Entities present on both sides, older name to newer name. */
  entities: EntityMatch[];
  /** Declared renames that matched nothing (a wrong name, or something that exists on one side only). */
  ignoredRenames: RenameSpec[];
}

const PROPERTY_FACETS: (keyof PropertySpec)[] = ['type', 'required', 'maxLength', 'precision', 'scale', 'generated', 'concurrencyToken', 'default', 'collection', 'comment'];

function same(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  return canonicalJson(a) === canonicalJson(b);
}

function sameList(a: readonly string[] | undefined, b: readonly string[] | undefined): boolean {
  if (!a || !b) return a === b;
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** Normalized Levenshtein similarity in [0, 1]. */
export function nameSimilarity(a: string, b: string): number {
  const x = a.toLowerCase();
  const y = b.toLowerCase();
  if (x === y) return 1;
  const prev = Array.from({ length: y.length + 1 }, (_, i) => i);
  for (let i = 1; i <= x.length; i++) {
    let diag = prev[0]!;
    prev[0] = i;
    for (let j = 1; j <= y.length; j++) {
      const temp = prev[j]!;
      prev[j] = Math.min(prev[j]! + 1, prev[j - 1]! + 1, diag + (x[i - 1] === y[j - 1] ? 0 : 1));
      diag = temp;
    }
  }
  return 1 - prev[y.length]! / Math.max(x.length, y.length);
}

function propertySignature(name: string, p: PropertySpec): string {
  return `${name}:${p.type}:${p.required}:${p.maxLength ?? ''}:${p.collection ?? ''}`;
}

function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 && b.size === 0) return 0;
  let shared = 0;
  for (const x of a) if (b.has(x)) shared++;
  return shared / (a.size + b.size - shared);
}

function entityFacets(e: EntitySpec | undefined, mapName: (n: string) => string): EntityFacets {
  const m: MappingSpec = e?.mapping ?? {};
  return {
    base: e?.base ? mapName(e.base) : null,
    strategy: m.strategy ?? null,
    discriminator: m.discriminator ?? null,
    discriminatorValue: m.discriminatorValue ?? null,
    keyless: e?.keyless ?? false,
    table: m.table === undefined ? null : m.table,
    schema: m.schema ?? null,
    view: m.view ?? null,
    json: m.json ?? null,
    comment: e?.comment ?? null,
  };
}

const INHERITANCE_FACETS: (keyof EntityFacets)[] = ['base', 'strategy', 'discriminator', 'discriminatorValue'];
const MAPPING_FACETS: (keyof EntityFacets)[] = ['keyless', 'table', 'schema', 'view', 'json', 'comment'];

class Differ {
  readonly changes: ModelChange[] = [];
  private readonly usedRenames = new Set<RenameSpec>();
  /** Per entity path (newer name): older property name to newer property name. */
  private readonly propertyRenames = new Map<string, Map<string, string>>();

  constructor(
    private readonly before: ModelSpec,
    private readonly after: ModelSpec,
    private readonly options: DiffOptions,
  ) {}

  run(): ModelDiff {
    const matches = this.matchEntities();
    const toAfter = new Map(matches.map((m) => [m.from, m.to]));
    const mapName = (n: string) => toAfter.get(n) ?? n;
    const collapse = this.options.collapseJoins !== false;
    const joinsBefore = new Set(collapse ? Object.keys(this.before.entities).filter((k) => isPureJoin(this.before, k)) : []);
    const joinsAfter = new Set(collapse ? Object.keys(this.after.entities).filter((k) => isPureJoin(this.after, k)) : []);

    for (const m of matches) {
      if (m.how !== 'name') this.changes.push({ kind: 'entity-renamed', from: m.from, to: m.to, how: m.how, confidence: m.confidence });
      this.diffEntity(m.to, this.before.entities[m.from]!, this.after.entities[m.to]!, mapName);
    }
    const matchedAfter = new Set(matches.map((m) => m.to));
    const matchedBefore = new Set(matches.map((m) => m.from));
    for (const key of Object.keys(this.after.entities)) {
      if (matchedAfter.has(key)) continue;
      const pair = joinsAfter.has(key) ? isPureJoin(this.after, key) : null;
      if (pair) this.changes.push({ kind: 'many-to-many-added', between: [pair[0].principal, pair[1].principal], join: key });
      else this.changes.push({ kind: 'entity-added', entity: key });
    }
    for (const key of Object.keys(this.before.entities)) {
      if (matchedBefore.has(key)) continue;
      const pair = joinsBefore.has(key) ? isPureJoin(this.before, key) : null;
      if (pair) this.changes.push({ kind: 'many-to-many-removed', between: [mapName(pair[0].principal), mapName(pair[1].principal)], join: key });
      else this.changes.push({ kind: 'entity-removed', entity: key });
    }

    this.diffRelationships(mapName, joinsBefore, joinsAfter, matchedBefore, matchedAfter);
    this.diffSequences();
    const ignoredRenames = (this.options.renames ?? []).filter((r) => !this.usedRenames.has(r));
    return { changes: this.changes, entities: matches, ignoredRenames };
  }

  // ------------------------------------------------------------------ entity matching

  private matchEntities(): EntityMatch[] {
    const before = this.before.entities;
    const after = this.after.entities;
    const matches: EntityMatch[] = [];
    const usedBefore = new Set<string>();
    const usedAfter = new Set<string>();
    const add = (from: string, to: string, how: MatchHow, confidence: number) => {
      matches.push({ from, to, how, confidence });
      usedBefore.add(from);
      usedAfter.add(to);
    };
    for (const k of Object.keys(before)) if (after[k]) add(k, k, 'name', 1);
    for (const r of this.options.renames ?? []) {
      if (!r.entity && before[r.from] && after[r.to] && !usedBefore.has(r.from) && !usedAfter.has(r.to)) {
        add(r.from, r.to, 'declared', 1);
        this.usedRenames.add(r);
      }
    }
    for (const h of this.options.hints ?? []) {
      if (h.kind !== 'table') continue;
      const from = Object.keys(before).find((k) => !usedBefore.has(k) && before[k]!.mapping?.table === h.from);
      const to = Object.keys(after).find((k) => !usedAfter.has(k) && after[k]!.mapping?.table === h.to);
      if (from && to) add(from, to, 'migration', 1);
    }
    // Similarity: same table, or mostly the same properties.
    const candidates: { from: string; to: string; score: number }[] = [];
    for (const [bk, b] of Object.entries(before)) {
      if (usedBefore.has(bk)) continue;
      for (const [ak, a] of Object.entries(after)) {
        if (usedAfter.has(ak)) continue;
        const sameTable = !!b.mapping?.table && b.mapping.table === a.mapping?.table && b.mapping.schema === a.mapping?.schema;
        const bKey = new Set(b.key ?? []);
        const aKey = new Set(a.key ?? []);
        const bSig = new Set(Object.entries(b.properties).filter(([n]) => !bKey.has(n)).map(([n, p]) => propertySignature(n, p)));
        const aSig = new Set(Object.entries(a.properties).filter(([n]) => !aKey.has(n)).map(([n, p]) => propertySignature(n, p)));
        const overlap = jaccard(bSig, aSig);
        const names = nameSimilarity(bk, ak);
        const score = sameTable ? 0.95 : overlap >= 0.5 ? 0.7 * overlap + 0.3 * names : 0;
        if (score >= 0.55) candidates.push({ from: bk, to: ak, score });
      }
    }
    candidates.sort((x, y) => y.score - x.score);
    for (const c of candidates) {
      if (usedBefore.has(c.from) || usedAfter.has(c.to)) continue;
      add(c.from, c.to, 'guess', Math.round(c.score * 100) / 100);
    }
    return matches;
  }

  // ------------------------------------------------------------------ entities

  private diffEntity(path: string, b: EntitySpec, a: EntitySpec, mapName: (n: string) => string): void {
    const bf = entityFacets(b, mapName);
    const af = entityFacets(a, (n) => n);
    const changed = (facets: (keyof EntityFacets)[]) => facets.filter((f) => !same(bf[f], af[f]));
    const pick = (f: EntityFacets, keys: string[]) => Object.fromEntries(keys.map((k) => [k, f[k as keyof EntityFacets]])) as Partial<EntityFacets>;
    const inheritance = changed(INHERITANCE_FACETS);
    if (inheritance.length) this.changes.push({ kind: 'inheritance-changed', entity: path, facets: inheritance, before: pick(bf, inheritance), after: pick(af, inheritance) });
    const mapping: string[] = changed(MAPPING_FACETS);
    if (this.options.includePhysical && !same(b.physical, a.physical)) mapping.push('physical');
    if (mapping.length) this.changes.push({ kind: 'entity-changed', entity: path, facets: mapping, before: pick(bf, mapping), after: pick(af, mapping) });
    this.diffContainer(path, b, a);
  }

  /** Properties, keys, indexes, checks, seed data, owned and complex types. Shared by entities and owned types. */
  private diffContainer(path: string, b: EntitySpec | OwnedSpec, a: EntitySpec | OwnedSpec): void {
    const renames = this.diffProperties(path, b.properties, a.properties, b, a);
    const mapProp = (n: string) => renames.get(n) ?? n;

    const bKey = (b.key ?? []).map(mapProp);
    const aKey = a.key ?? [];
    if (!sameList(bKey, aKey) && (b.key || a.key)) this.changes.push({ kind: 'key-changed', entity: path, before: bKey, after: aKey });
    const bAlt = ((b as EntitySpec).alternateKeys ?? []).map((k) => k.map(mapProp));
    const aAlt = (a as EntitySpec).alternateKeys ?? [];
    for (const k of aAlt) if (!bAlt.some((x) => sameList(x, k))) this.changes.push({ kind: 'key-added', entity: path, properties: k });
    for (const k of bAlt) if (!aAlt.some((x) => sameList(x, k))) this.changes.push({ kind: 'key-removed', entity: path, properties: k });

    this.diffIndexes(path, b.indexes ?? [], a.indexes ?? [], mapProp);

    const bChecks = new Map((b.checks ?? []).map((c) => [c.name, c.sql]));
    const aChecks = new Map((a.checks ?? []).map((c) => [c.name, c.sql]));
    for (const [name, sql] of aChecks) {
      if (!bChecks.has(name)) this.changes.push({ kind: 'check-added', entity: path, name, sql });
      else if (bChecks.get(name) !== sql) this.changes.push({ kind: 'check-changed', entity: path, name, before: bChecks.get(name)!, after: sql });
    }
    for (const [name, sql] of bChecks) if (!aChecks.has(name)) this.changes.push({ kind: 'check-removed', entity: path, name, sql });

    if (!same(b.seed ?? [], a.seed ?? [])) this.changes.push({ kind: 'seed-changed', entity: path, before: b.seed?.length ?? 0, after: a.seed?.length ?? 0 });

    // Owned types, by navigation.
    const bOwned = b.owned ?? {};
    const aOwned = a.owned ?? {};
    for (const [nav, ao] of Object.entries(aOwned)) {
      const bo = bOwned[nav];
      if (!bo) {
        this.changes.push({ kind: 'owned-added', entity: path, navigation: nav, type: ao.type });
        continue;
      }
      const facets: string[] = [];
      if (bo.type !== ao.type) facets.push('type');
      if (!!bo.collection !== !!ao.collection) facets.push('collection');
      if (!!bo.required !== !!ao.required) facets.push('required');
      if ((bo.mapping?.json ?? null) !== (ao.mapping?.json ?? null)) facets.push('json');
      if ((bo.mapping?.table ?? null) !== (ao.mapping?.table ?? null)) facets.push('table');
      if (!sameList(bo.ownerKey.map(mapProp), ao.ownerKey)) facets.push('ownerKey');
      if (facets.length) this.changes.push({ kind: 'owned-changed', entity: path, navigation: nav, facets });
      this.diffContainer(`${path}.${nav}`, bo, ao);
    }
    for (const [nav, bo] of Object.entries(bOwned)) if (!aOwned[nav]) this.changes.push({ kind: 'owned-removed', entity: path, navigation: nav, type: bo.type });

    this.diffComplex(path, b.complex ?? {}, a.complex ?? {});
  }

  private diffComplex(path: string, bComplex: Record<string, ComplexSpec>, aComplex: Record<string, ComplexSpec>): void {
    for (const [name, ac] of Object.entries(aComplex)) {
      const bc = bComplex[name];
      if (!bc) {
        this.changes.push({ kind: 'complex-added', entity: path, property: name, type: ac.type });
        continue;
      }
      const facets: string[] = [];
      if (bc.type !== ac.type) facets.push('type');
      if (!!bc.collection !== !!ac.collection) facets.push('collection');
      if (bc.required !== ac.required) facets.push('required');
      if ((bc.mapping?.json ?? null) !== (ac.mapping?.json ?? null)) facets.push('json');
      if (facets.length) this.changes.push({ kind: 'complex-changed', entity: path, property: name, facets });
      this.diffProperties(`${path}.${name}`, bc.properties, ac.properties, null, null);
      this.diffComplex(`${path}.${name}`, bc.complex ?? {}, ac.complex ?? {});
    }
    for (const [name, bc] of Object.entries(bComplex)) if (!aComplex[name]) this.changes.push({ kind: 'complex-removed', entity: path, property: name, type: bc.type });
  }

  private diffProperties(
    path: string,
    bProps: Record<string, PropertySpec>,
    aProps: Record<string, PropertySpec>,
    bOwner: EntitySpec | OwnedSpec | null,
    aOwner: EntitySpec | OwnedSpec | null,
  ): Map<string, string> {
    const pairs = new Map<string, { to: string; how: MatchHow; confidence: number }>();
    const usedAfter = new Set<string>();
    const pair = (from: string, to: string, how: MatchHow, confidence: number) => {
      pairs.set(from, { to, how, confidence });
      usedAfter.add(to);
    };
    for (const n of Object.keys(bProps)) if (aProps[n]) pair(n, n, 'name', 1);
    for (const r of this.options.renames ?? []) {
      if (r.entity === path && bProps[r.from] && aProps[r.to] && !pairs.has(r.from) && !usedAfter.has(r.to)) {
        pair(r.from, r.to, 'declared', 1);
        this.usedRenames.add(r);
      }
    }
    const table = (aOwner as EntitySpec | null)?.mapping?.table ?? (bOwner as EntitySpec | null)?.mapping?.table;
    const column = (n: string, p: PropertySpec) => p.physical?.column ?? n;
    for (const h of this.options.hints ?? []) {
      if (h.kind !== 'column' || !table || h.table !== table) continue;
      const from = Object.entries(bProps).find(([n, p]) => !pairs.has(n) && column(n, p) === h.from)?.[0];
      const to = Object.entries(aProps).find(([n, p]) => !usedAfter.has(n) && column(n, p) === h.to)?.[0];
      if (from && to) pair(from, to, 'migration', 1);
    }
    // Guess: a removed and an added property of the same type.
    const removed = Object.keys(bProps).filter((n) => !pairs.has(n));
    const added = Object.keys(aProps).filter((n) => !usedAfter.has(n));
    const typeOf = (p: PropertySpec) => `${p.type}:${p.collection ?? false}`;
    for (const from of removed) {
      const options = added.filter((to) => !usedAfter.has(to) && typeOf(aProps[to]!) === typeOf(bProps[from]!));
      if (options.length === 0) continue;
      const sameTypeRemoved = removed.filter((r) => !pairs.has(r) && typeOf(bProps[r]!) === typeOf(bProps[from]!));
      const best = options.map((to) => ({ to, sim: nameSimilarity(from, to) })).sort((x, y) => y.sim - x.sim)[0]!;
      const unique = options.length === 1 && sameTypeRemoved.length === 1;
      if (unique || best.sim >= 0.5) pair(from, best.to, 'guess', Math.round((unique ? 0.6 + 0.3 * best.sim : 0.5 + 0.3 * best.sim) * 100) / 100);
    }

    const renames = new Map<string, string>();
    for (const [from, { to, how, confidence }] of pairs) {
      renames.set(from, to);
      if (how !== 'name') this.changes.push({ kind: 'property-renamed', entity: path, from, to, how, confidence });
      const bp = bProps[from]!;
      const ap = aProps[to]!;
      const facets: string[] = PROPERTY_FACETS.filter((f) => !same(bp[f], ap[f]));
      if (this.options.includePhysical && !same(bp.physical, ap.physical)) facets.push('physical');
      if (facets.length) this.changes.push({ kind: 'property-changed', entity: path, property: to, facets, before: bp, after: ap });
    }
    for (const n of Object.keys(aProps)) if (!usedAfter.has(n)) this.changes.push({ kind: 'property-added', entity: path, property: n, spec: aProps[n]! });
    for (const n of Object.keys(bProps)) if (!pairs.has(n)) this.changes.push({ kind: 'property-removed', entity: path, property: n, spec: bProps[n]! });
    this.propertyRenames.set(path, renames);
    return renames;
  }

  private diffIndexes(path: string, bList: IndexSpec[], aList: IndexSpec[], mapProp: (n: string) => string): void {
    const id = (ix: IndexSpec, props: string[]) => `${props.join(',')}|${ix.name ?? ''}`;
    const bMap = new Map(bList.map((ix) => [id(ix, ix.properties.map(mapProp)), ix]));
    const aMap = new Map(aList.map((ix) => [id(ix, ix.properties), ix]));
    for (const [key, ax] of aMap) {
      const bx = bMap.get(key);
      if (!bx) {
        this.changes.push({ kind: 'index-added', entity: path, properties: ax.properties, spec: ax });
        continue;
      }
      const facets: string[] = [];
      if (!!bx.unique !== !!ax.unique) facets.push('unique');
      if ((bx.filter ?? null) !== (ax.filter ?? null)) facets.push('filter');
      if (!same(bx.descending ?? null, ax.descending ?? null)) facets.push('descending');
      if (this.options.includePhysical && !same(bx.physical, ax.physical)) facets.push('physical');
      if (facets.length) this.changes.push({ kind: 'index-changed', entity: path, properties: ax.properties, facets, before: bx, after: ax });
    }
    for (const [key, bx] of bMap) if (!aMap.has(key)) this.changes.push({ kind: 'index-removed', entity: path, properties: bx.properties.map(mapProp), spec: bx });
  }

  // ------------------------------------------------------------------ relationships

  private diffRelationships(
    mapName: (n: string) => string,
    joinsBefore: Set<string>,
    joinsAfter: Set<string>,
    matchedBefore: Set<string>,
    matchedAfter: Set<string>,
  ): void {
    // Newer-side identity of an older relationship: rename entities and foreign key properties.
    const mapPath = (p: string) => {
      const { root, rest } = rootOfPath(this.before, p);
      return [mapName(root), ...rest].join('.');
    };
    const identity = (dependent: string, fk: string[], principal: string) => `${dependent}|${fk.join(',')}|${principal}`;
    const beforeRels = this.before.relationships.map((r) => {
      const dependent = mapPath(r.dependent);
      const renames = this.propertyRenames.get(dependent);
      const fk = r.foreignKey.map((p) => renames?.get(p) ?? p);
      return { r, dependent, principal: mapPath(r.principal), fk, id: identity(dependent, fk, mapPath(r.principal)) };
    });
    const afterRels = this.after.relationships.map((r) => ({ r, id: identity(r.dependent, r.foreignKey, r.principal) }));
    const afterById = new Map(afterRels.map((x) => [x.id, x.r]));
    const beforeIds = new Set(beforeRels.map((x) => x.id));
    // Relationships of a join shown as many-to-many, or of a whole added/removed entity, are covered by that change.
    const hiddenAfter = (r: RelationshipSpec) => joinsAfter.has(rootOfPath(this.after, r.dependent).root) && !matchedAfter.has(rootOfPath(this.after, r.dependent).root);
    const hiddenBefore = (r: RelationshipSpec) => joinsBefore.has(rootOfPath(this.before, r.dependent).root) && !matchedBefore.has(rootOfPath(this.before, r.dependent).root);

    for (const b of beforeRels) {
      const a = afterById.get(b.id);
      if (!a) {
        if (!hiddenBefore(b.r)) this.changes.push({ kind: 'relationship-removed', dependent: b.dependent, principal: b.principal, foreignKey: b.fk, relationship: b.r });
        continue;
      }
      const facets: string[] = [];
      if (b.r.cardinality !== a.cardinality) facets.push('cardinality');
      if (b.r.required !== a.required) facets.push('required');
      if (!!b.r.requiredDependent !== !!a.requiredDependent) facets.push('requiredDependent');
      if (b.r.onDelete !== a.onDelete) facets.push('onDelete');
      if ((b.r.dependentNavigation ?? null) !== (a.dependentNavigation ?? null)) facets.push('dependentNavigation');
      if ((b.r.principalNavigation ?? null) !== (a.principalNavigation ?? null)) facets.push('principalNavigation');
      if (!sameList(b.r.principalKey, a.principalKey)) facets.push('principalKey');
      if (this.options.includePhysical && !same(b.r.physical, a.physical)) facets.push('physical');
      if (facets.length) this.changes.push({ kind: 'relationship-changed', dependent: a.dependent, principal: a.principal, foreignKey: a.foreignKey, facets, before: b.r, after: a });
    }
    for (const a of afterRels) {
      if (!beforeIds.has(a.id) && !hiddenAfter(a.r)) {
        this.changes.push({ kind: 'relationship-added', dependent: a.r.dependent, principal: a.r.principal, foreignKey: a.r.foreignKey, relationship: a.r });
      }
    }
  }

  private diffSequences(): void {
    const key = (s: SequenceSpec) => `${s.schema ?? ''}.${s.name}`;
    const b = new Map((this.before.sequences ?? []).map((s) => [key(s), s]));
    const a = new Map((this.after.sequences ?? []).map((s) => [key(s), s]));
    for (const [k, s] of a) {
      const old = b.get(k);
      if (!old) this.changes.push({ kind: 'sequence-added', name: s.name });
      else {
        const facets = (['type', 'start', 'increment', 'min', 'max', 'cyclic'] as const).filter((f) => !same(old[f], s[f]));
        if (facets.length) this.changes.push({ kind: 'sequence-changed', name: s.name, facets });
      }
    }
    for (const [k, s] of b) if (!a.has(k)) this.changes.push({ kind: 'sequence-removed', name: s.name });
  }
}

export function diffModels(before: ModelSpec, after: ModelSpec, options: DiffOptions = {}): ModelDiff {
  return new Differ(before, after, options).run();
}

/** The entity path a change is about (for grouping), or null for model-level changes. */
export function changeEntity(c: ModelChange): string | null {
  switch (c.kind) {
    case 'entity-renamed':
      return c.to;
    case 'relationship-added':
    case 'relationship-removed':
    case 'relationship-changed':
      return c.dependent;
    case 'many-to-many-added':
    case 'many-to-many-removed':
      return c.between[0];
    case 'sequence-added':
    case 'sequence-removed':
    case 'sequence-changed':
      return null;
    default:
      return c.entity;
  }
}
